import React from 'react';
import { NavDirections } from './types';

const THRESHOLD = [0.01, 0.8];

/**
 * Local replacement for react-use's `useIntersection` (react-use is not
 * installable/sanctioned in Valkyrie — memory.md: "reimplement locally,
 * SSR-guarded"). Same contract: observes the ref'd element with an
 * IntersectionObserver and returns the latest entry (null before the first
 * callback / when unsupported, e.g. jsdom without IO or SSR).
 */
export const useIntersection = (
    ref: React.RefObject<HTMLElement | null>,
    options: IntersectionObserverInit,
): IntersectionObserverEntry | null => {
    const [entry, setEntry] =
        React.useState<IntersectionObserverEntry | null>(null);

    React.useEffect(() => {
        const node = ref.current;
        if (
            !node ||
            typeof window === 'undefined' ||
            typeof IntersectionObserver === 'undefined'
        ) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => setEntry(entries[entries.length - 1] ?? null),
            options,
        );
        observer.observe(node);

        return () => {
            setEntry(null);
            observer.disconnect();
        };
        // Matching react-use: re-observe when the observer inputs change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref.current, options.root, options.rootMargin, options.threshold]);

    return entry;
};

interface IsInViewPort {
    hasEnteredViewport: boolean;
    isVisible: boolean;
}

export const useIsInViewPort = ({
    ref,
    trackVisibility,
}: {
    ref: React.RefObject<HTMLElement | null>;
    trackVisibility?: boolean;
}): IsInViewPort => {
    const [hasEnteredViewport, setHasEnteredViewport] = React.useState(false);
    const [isVisible, setIsVisible] = React.useState(false);

    const intersection = useIntersection(ref, {
        root: null,
        rootMargin: '100% 0px 100% 50px',
        threshold: THRESHOLD,
    });

    if (intersection?.isIntersecting && !hasEnteredViewport) {
        setHasEnteredViewport(true);
    }

    if (trackVisibility && intersection) {
        if (intersection.intersectionRatio >= 0.8 && !isVisible) {
            setIsVisible(true);
        } else if (intersection.intersectionRatio < 0.8 && isVisible) {
            setIsVisible(false);
        }
    }

    return { hasEnteredViewport, isVisible };
};

export const useSetDisabledNav = ({
    isLastSlide,
    isFirstSlide,
    isVisible,
    setDisabledNav,
    fullyEnableNavigation = false,
    isFirstPage = false,
    isLastPage = false,
}: {
    isLastSlide: boolean;
    isFirstSlide: boolean;
    isVisible: boolean;
    setDisabledNav: (direction?: NavDirections) => void;
    fullyEnableNavigation?: boolean;
    isFirstPage?: boolean;
    isLastPage?: boolean;
}) => {
    const enabled = isLastSlide || isFirstSlide;
    React.useEffect(() => {
        if (enabled && !fullyEnableNavigation) {
            if (isVisible) {
                setDisabledNav(
                    isLastSlide ? NavDirections.Right : NavDirections.Left,
                );
            } else {
                setDisabledNav(undefined);
            }
        } else if (fullyEnableNavigation) {
            if (isFirstPage && isVisible && isFirstSlide) {
                setDisabledNav(NavDirections.Left);
            } else if (isLastPage && isVisible && isLastSlide) {
                setDisabledNav(NavDirections.Right);
            } else {
                setDisabledNav(undefined);
            }
        }
    }, [
        isLastSlide,
        isVisible,
        enabled,
        fullyEnableNavigation,
        setDisabledNav,
        isFirstPage,
        isLastPage,
        isFirstSlide,
    ]);
};
